import { Column } from '@app/components/data-grid/data-grid';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useSpaceContext } from '@app/features/analytics/hooks/use-space-context';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect } from 'react';

import { DataGrid } from '../../components';
import { ErrorScreen } from '../../components/error-states/error-screen';
import { NoFilterResults } from '../../components/error-states/no-filter-results';
import { NoResults } from '../../components/error-states/no-results';
import { usePaginatedFetch } from '../../hooks/use-paginated-fetch';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { fetchEvents } from '../../services/endpoints';
import {
  formatDate,
  getNextPageCount,
  minutesToReadableTime,
} from '../../utils';
import { EngagementScoreTemplate } from '../registrants/engagement-score-template';
import { CreatorAvatarTemplate } from './creator-avatar-template';
import { EventNameTemplate } from './event-name-template';
import { EventTypeTemplate } from './event-type-template';
import { LoaderSkeleton } from './loader-skeleton';
import { EventType } from './types';

export const EventColumns = (): Column<EventType>[] => {
  const { t } = useTranslation();
  return [
    {
      text: t('analytics.event'),
      name: 'name',
      template: EventNameTemplate,
      width: '300px',
    },
    {
      text: t('analytics.date'),
      name: 'startTime',
      width: '220px',
      formatter: (value: string, rowData?: EventType) => {
        return (
          formatDate(value || '') + ' -> ' + formatDate(rowData?.endTime || '')
        );
      },
    },
    {
      text: t('analytics.event-type'),
      name: 'venueType',
      template: EventTypeTemplate,
    },
    {
      text: t('analytics.avg-engagement-score'),
      name: 'engagementScore',
      sort: false,
      template: EngagementScoreTemplate,
    },
    {
      text: t('analytics.avg-time-spent'),
      name: 'averageMinutes',
      sort: false,
      formatter: value => {
        return !!value && value !== 'null'
          ? minutesToReadableTime(Number(value))
          : '';
      },
    },
    { text: t('analytics.registrants'), name: 'registrantsNumber' },
    { text: t('analytics.attendees'), name: 'attendeesNumber' },
    {
      text: t('analytics.turnout'),
      name: 'turnout',
      formatter: value => `${value}%`,
    },
    {
      text: t('analytics.events-tab.creator-email'),
      name: 'creatorEmail',
      template: CreatorAvatarTemplate,
    },
  ];
};

export const Events = () => {
  const { state, dispatch } = useAnalyticsContext();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { spaceExternalId } = useSpaceContext();

  const config = {
    dateRange: state.dateRange,
    startDate: state.startDate,
    endDate: state.endDate,
    eventTypes: state.eventTypes,
    name: state.eventSortName,
    direction: state.eventSortDirection,
    searchText: state.searchText,
    selectedEvents: state.selectedEvents,
    spaceExternalId,
  };

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoad,
  } = usePaginatedFetch(
    { ...config },
    `events-${orgId}`,
    useCallback(fetchEvents, []),
  );
  const { track } = useAnalytics();

  const handleSort = (columnKey: string, direction: 'asc' | 'desc' | '') => {
    dispatch({
      type: 'update events sort key and direction',
      payload: { name: columnKey, direction: direction },
    });
    track('ola_events_tab_sorting_click', {
      columnKey: columnKey,
      direction: direction,
    });
  };

  const fetchMore = () => {
    if (!isLoading && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };
  const result = {
    eventsData: data?.pages.flatMap(page => page.events) || [],
    pagination: data?.pages[data.pages.length - 1]?.pagination || {},
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <DataGrid<EventType>
      columns={EventColumns()}
      data={result.eventsData || []}
      showLoader={(isLoading || isFetchingNextPage) && !isError}
      loader={LoaderSkeleton}
      loaderCount={getNextPageCount(result.pagination)}
      onSort={handleSort}
      fetchMore={fetchMore}
      sortConfig={{
        name: state.eventSortName as keyof EventType,
        direction: state.eventSortDirection as '' | 'asc' | 'desc',
      }}
      noResults={() => (isInitialLoad ? NoResults : NoFilterResults)}
      error={props => <ErrorScreen {...props} showButton={true} />}
      isError={isError}
    />
  );
};
